// x-request-id and x-app on every call backend pages make to their own origin, and the reference
// block shown when one fails: the ID, the UTC time and the creator, as on server error pages.

const REQUEST_ID = 'x-request-id';
const APP = 'x-app';

function meta(name) {
    const element = document.querySelector(`meta[name="${name}"]`);

    return element ? element.getAttribute('content') : '';
}

function appName() {
    return meta(APP) || 'platform';
}

export function newRequestId() {
    // randomUUID exists only in secure contexts and newer browsers
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
}

export function isSameOrigin(url) {
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    } catch (e) {
        return false;
    }
}

function headerName(headers, name) {
    return Object.keys(headers).find((key) => key.toLowerCase() === name);
}

export function installAjaxPrefilter($) {
    $.ajaxPrefilter((options, originalOptions, jqXHR) => {
        if (options.crossDomain) {
            return;
        }

        // jQuery sends options.headers after the prefilters, so a header set here is kept even
        // when the call passes its own beforeSend
        const headers = Object.assign({}, options.headers);
        const presetId = headerName(headers, REQUEST_ID);

        if (undefined === presetId) {
            headers[REQUEST_ID] = newRequestId();
        }

        if (undefined === headerName(headers, APP)) {
            headers[APP] = appName();
        }

        options.headers = headers;
        jqXHR.requestId = headers[undefined === presetId ? REQUEST_ID : presetId];
    });
}

// The ID the server logged (the edge may still replace ours), else the one we sent.
export function ajaxRequestId(jqXHR) {
    return jqXHR.getResponseHeader(REQUEST_ID) || jqXHR.requestId || '';
}

export function installFetchWrapper(onServerError) {
    const originalFetch = window.fetch;

    if (typeof originalFetch !== 'function' || originalFetch.addsRequestId) {
        return;
    }

    const fetchWithRequestId = function (input, init) {
        const request = input instanceof Request ? input : null;

        if (!isSameOrigin(request ? request.url : String(input))) {
            return originalFetch.call(window, input, init);
        }

        let headers;

        try {
            // init.headers replaces a Request's own headers, so start from whichever fetch would send
            headers = new Headers(init && undefined !== init.headers ? init.headers : (request ? request.headers : undefined));
        } catch (e) {
            // Headers the browser rejects: let fetch reject them as it would without this wrapper
            return originalFetch.call(window, input, init);
        }

        if (!headers.has(REQUEST_ID)) {
            headers.set(REQUEST_ID, newRequestId());
        }

        if (!headers.has(APP)) {
            headers.set(APP, appName());
        }

        const sentId = headers.get(REQUEST_ID);

        return originalFetch.call(window, input, Object.assign({}, init, {headers})).then((response) => {
            if (response.status >= 500) {
                try {
                    onServerError(response.headers.get(REQUEST_ID) || sentId);
                } catch (e) {
                    // The caller still gets its response
                }
            }

            return response;
        });
    };

    fetchWithRequestId.addsRequestId = true;
    window.fetch = fetchWithRequestId;
}

function copyText(element, hint) {
    const copied = () => {
        hint.textContent = 'Copied';
    };
    const copyBySelection = () => {
        const range = document.createRange();
        const selection = window.getSelection();

        range.selectNodeContents(element);
        selection.removeAllRanges();
        selection.addRange(range);

        try {
            if (document.execCommand('copy')) {
                copied();
            }
        } catch (e) {
            // The text stays selected for a manual copy.
        }
    };

    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(element.textContent).then(copied, copyBySelection);

        return;
    }

    copyBySelection();
}

export function errorReferenceElement(requestId) {
    const lines = [requestId, new Date().toISOString().slice(0, 19).replace('T', ' ') + ' UTC'];
    const principals = meta('error-reference-principals');

    if (principals) {
        lines.push(principals);
    }

    const wrapper = document.createElement('div');
    const lead = document.createElement('div');
    const block = document.createElement('button');
    const hint = document.createElement('div');

    lead.className = 'error-reference-lead';
    lead.textContent = 'Something went wrong on our side. If you contact support, send them this reference:';
    block.type = 'button';
    block.className = 'error-reference';
    block.textContent = lines.filter(Boolean).join('\n');
    hint.className = 'error-reference-hint';
    hint.textContent = 'Click to copy';
    block.addEventListener('click', () => copyText(block, hint));
    wrapper.append(lead, block, hint);

    return wrapper;
}
